import {
  coercePreferences,
  type PlannerPreferenceOptions,
} from "./preferences";
import { PLANNER_SPORTS, type PlannerSport, type SessionPreferences } from "./types";
import {
  isSupProfileKey,
  normaliseSupProfileKey,
  type SupProfileKey,
} from "../conditions/sport-profiles";
import { conditionsSpotHref } from "../seo/conditions-links";

const PLANNER_SPORT_SET = new Set<string>(PLANNER_SPORTS);

const CONTEXT_TO_PLANNER_SPORT: Record<string, PlannerSport | null> = {
  surf: "surf",
  bodyboarding: "bodyboarding",
  wind: "wind",
  foil: "foil",
  "downwind-foil": "foil",
  "wing-foil": "foil",
  sup: "sup",
  swim: "swim",
  swimming: "swim",
  sailing: "sailing",
  "sailing-club": "sailing",
  "sailing-hp": "sailing",
  diving: "diving",
  ski: null,
  piste: null,
  touring: null,
  mtb: null,
};

export type PlannerPreferenceRow = {
  sport: string;
  values: unknown;
  updated_at?: string | null;
};

function normaliseKey(value: string | null | undefined): string | null {
  const key = value?.trim().toLowerCase().replace(/_/g, "-");
  return key || null;
}

export function plannerPreferenceStorageKey(
  sport: PlannerSport,
  _profile?: SupProfileKey,
): string {
  return `pw-session-prefs-${sport}`;
}

export function isPlannerSport(value: string | null | undefined): value is PlannerSport {
  const key = normaliseKey(value);
  return key ? PLANNER_SPORT_SET.has(key) : false;
}

export function assertPlannerSport(value: string): PlannerSport {
  const key = normaliseKey(value);
  if (!key || !PLANNER_SPORT_SET.has(key)) {
    throw new Error(`Unknown planner sport: ${value}`);
  }
  return key as PlannerSport;
}

export function normalisePlannerPreferenceRow(
  row: PlannerPreferenceRow,
  options: PlannerPreferenceOptions = {},
): SessionPreferences {
  const rowKey = normaliseKey(row.sport);
  const profile = rowKey && isSupProfileKey(rowKey) ? rowKey : null;
  const sport = profile ? "sup" : assertPlannerSport(row.sport);
  const rawValues = row.values && typeof row.values === "object"
    ? row.values as Record<string, unknown>
    : {};
  return coercePreferences(
    sport,
    { values: profile ? { ...rawValues, profile } : rawValues },
    options,
  );
}

/**
 * Coerce local or account preferences against server-granted profile access.
 * An exact URL may select a named profile only when that same key appears in
 * the verified response payload.
 */
export function plannerPreferencesForAccess(
  sport: PlannerSport,
  raw: unknown,
  availableSupProfiles: readonly SupProfileKey[] = [],
  contextKey?: string | null,
): SessionPreferences {
  const rawContainer = raw && typeof raw === "object" && "values" in raw
    ? (raw as { values?: unknown }).values
    : raw;
  const rawValues = rawContainer && typeof rawContainer === "object"
    ? rawContainer as Record<string, unknown>
    : {};
  const canonicalContextKey = normaliseKey(contextKey);
  const parsedContextProfile = canonicalContextKey
    ? normaliseSupProfileKey(canonicalContextKey)
    : null;
  const requestedProfile = sport === "sup" && parsedContextProfile &&
    availableSupProfiles.includes(parsedContextProfile)
    ? parsedContextProfile
    : null;
  return coercePreferences(
    sport,
    {
      values: requestedProfile
        ? { ...rawValues, profile: requestedProfile }
        : rawValues,
    },
    { availableSupProfiles },
  );
}

export function plannerSportFromContextKey(value: string | null | undefined): PlannerSport | null {
  const key = normaliseKey(value);
  if (!key) return null;
  if (isSupProfileKey(key)) return "sup";
  return CONTEXT_TO_PLANNER_SPORT[key] ?? null;
}

export function plannerSportSupportedByLocationType(
  sport: PlannerSport,
  locationType: string | null | undefined,
): boolean {
  const key = normaliseKey(locationType);
  if (key === "sailing" || key === "sailing-club" || key === "sailing-hp") {
    return sport === "sailing";
  }
  if (key === "surf" || key === "coastal") return sport !== "sailing";
  return false;
}

export function plannerSportUrlKey(
  sport: PlannerSport,
  profile?: SupProfileKey,
): string {
  if (sport === "sup" && profile) {
    return normaliseSupProfileKey(profile) ?? "sup-touring";
  }
  if (sport === "swim") return "swimming";
  return sport;
}

export function sessionPlannerHref(
  slug: string,
  sport: PlannerSport,
  profile?: SupProfileKey,
): string {
  return `/conditions/${sport === "sailing" ? "sailing/" : ""}${slug}?sport=${plannerSportUrlKey(sport, profile)}&planner=1#session-planner`;
}

export function conditionsSportHref(
  slug: string,
  sport: PlannerSport,
  profile?: SupProfileKey,
): string {
  // Canonical spot URL; the lens rides in the fragment (see lib/seo/conditions-links).
  return conditionsSpotHref(slug, plannerSportUrlKey(sport, profile));
}
